/* eslint-disable @typescript-eslint/no-explicit-any */
import { kv } from '@vercel/kv';
import isEmpty from 'lodash/isEmpty';
import type { NextPageContext } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import type { Params } from 'next/dist/shared/lib/router/utils/route-matcher';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useDispatch } from 'react-redux';
import {
  getCountryStaticProps,
  getCountryTopNamesStaticProps,
} from 'src/actions/countryActions';
import {
  searchForAllCountries,
  searchForAllCountriesStaticProps,
} from 'src/actions/searchActions';
import FunnelDiagram from 'src/components/diagrams/data-funnel';
import PieChartDiagram from 'src/components/diagrams/pie-chart';
import ListWithLinks from 'src/components/list/list-with-links';
import { NavFooter } from 'src/components/nav/navFooter.component';
import type { DataWorldChartTypes } from 'src/types/types';
import { convertToUrl } from 'src/utils/convertToUrl';
import InformationOutlineIcon from '../../assets/InformationOutlineIcon';
import RecordsStatsChart, {
  fetchChartData,
} from '../../components/chart/RecordsStatsChart';
import StandardSEOWrapper from '../../components/layout/standardSEOWrapper.component';
import ReactTooltip from '../../components/react-tooltip/ReactTooltip';
import RecordedCountriesTable from '../../components/table/recorded-countries-table';
import { useRecordedPopulation } from '../../hooks/useRecordedPopulation';
import { actions as searchActions } from '../../reducers/searchReducer';
import type { CountryData } from '../../types/country';
import type { WorldData } from '../../types/world';
import { cn } from '../../utils/cn';
import { formatName, formatNumber } from '../../utils/format';

export const mapTopNames = (
  type: 'first' | 'last',
  countryData: CountryData | WorldData
) => {
  const names =
    type === 'first'
      ? countryData?.top_first_names
      : countryData?.top_last_names;
  return names
    ?.map((name: any) => {
      return {
        id: convertToUrl(name.name),
        label: formatName(name.name),
        value: name.count,
        percentage: name.percentage,
      };
    })
    .sort((a: any, b: any) => b.value - a.value);
};

export default function Country({
  countries,
  country,
  countryData,
  chartData,
}: {
  countries: DataWorldChartTypes[];
  country: DataWorldChartTypes;
  countryData: CountryData;
  chartData: Record<
    string,
    { categories: string[]; series: { y: number; created: number }[] }
  >;
}) {
  const [firstNamesData, setFirstNamesData] = useState<any>(
    mapTopNames('first', countryData)
  );
  const [lastNamesData, setLastNamesData] = useState<any>(
    mapTopNames('last', countryData)
  );
  const [agePyramidData, setAgePyramidData] = useState<any>(null);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(12);
  const dispatch = useDispatch();
  const router = useRouter();

  useEffect(() => {
    if (countries?.length === 0) {
      searchForAllCountries().then((res) => {
        dispatch(searchActions.setAllCountries({ allCountries: res }));
      });
    }
  }, []);

  useEffect(() => {
    // We should reset the age pyramid data, top last names and top first names if there is none, otherwise the last state will be displayed
    if (!isEmpty(countryData)) {
      if (countryData?.age_pyramid) {
        getAgePyramidData();
      } else if (!countryData?.age_pyramid) {
        setAgePyramidData([]);
      }

      if (countryData?.top_first_names) {
        setFirstNamesData(mapTopNames('first', countryData));
      } else if (!countryData?.top_first_names) {
        setFirstNamesData([]);
      }

      if (countryData?.top_last_names) {
        setLastNamesData(mapTopNames('last', countryData));
      } else if (!countryData?.top_last_names) {
        setLastNamesData([]);
      }
    }
  }, [countryData]);

  useEffect(() => {
    // Check if the page is valid
    if (
      router.query.page &&
      (+router.query.page < Math.ceil(lastNamesData?.length / pageSize) ||
        +router.query.page > 0 ||
        !isNaN(+router.query.page))
    ) {
      setPage(+router.query.page);
    } else {
      setPage(1);
      if (router.query.page) {
        router.push({
          pathname: router.pathname,
          query: { ...router.query, page: '1' },
        });
      }
    }
  }, [router.query.page]);

  useEffect(() => {
    // Check if the page size is valid
    if (
      router.query.pageSize &&
      !isNaN(+router.query.pageSize) &&
      +router.query.pageSize > 0 &&
      +router.query.pageSize < lastNamesData?.length
    ) {
      setPageSize(+router.query.pageSize);
    } else {
      setPageSize(12);
      if (router.query.pageSize) {
        router.push({
          pathname: router.pathname,
          query: { ...router.query, pageSize: 12 },
        });
      }
    }
  }, [router.query.pageSize]);

  const sexRatioDataFemale =
    countryData?.female_count > 0 && countryData?.male_count > 0
      ? [
          {
            label: 'Female',
            value: parseInt(
              (
                (100 * countryData?.female_count) /
                (countryData?.male_count + countryData?.female_count)
              ).toFixed(0)
            ),
            fill: '#aae5d4',
            text: '#1f5048',
          },
          {
            label: 'Male',
            value: parseInt(
              (
                (100 * countryData?.male_count) /
                (countryData?.male_count + countryData?.female_count)
              ).toFixed(0)
            ),
            fill: '#257c6b',
            text: 'white',
          },
        ]
      : [];

  const getAgePyramidData = () => {
    const agePyramidData = countryData?.age_pyramid?.map((entry, i) => ({
      id: i,
      label: entry?.start + '-' + entry?.end,
      value: entry?.count,
    }));

    setAgePyramidData(agePyramidData?.reverse());
  };

  const handleNameClick = (href: string) => {
    dispatch(
      searchActions.setRedirectedFromCountry({
        redirectedFrom: router.query.slug,
      })
    );
    router.push(href);
  };

  const recordedPopulation = useRecordedPopulation({
    selectedCountry: country,
    lastRun: countryData?.statistics?.stats_run?.last_run,
    countryData: countryData,
  });

  const { t } = useTranslation(['country-slug']);

  return (
    <div className={`flex min-h-screen flex-col bg-[#FAFAFA]`}>
      <StandardSEOWrapper
        title={t('country-slug:seo.title', { countryName: country?.name })}
        description={t('country-slug:seo.description', {
          countryName: country?.name,
        })}
      />
      {!isEmpty(countryData) ? (
        <div
          className={`flex flex-col px-[20px] max-md:bg-[#FAFAFA] md:px-[45px] tablet:px-[80px]`}
        >
          <div className="flex justify-between pb-6 pt-8 text-gray-950 md:py-[40px]">
            <div className="flex flex-col gap-1 max-md:w-full">
              <div className="flex flex-col gap-3">
                <ReactCountryFlag
                  countryCode={country?.iso_2}
                  svg
                  cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/1x1/"
                  cdnSuffix="svg"
                  style={{
                    width: '40px',
                    borderRadius: '12px',
                    height: '40px',
                  }}
                />
                <p className="text-2xl font-semibold xl:text-[32px] xl:leading-10">
                  {country.name}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 xl:pt-1">
                <p className="text-2xl font-medium tabular-nums text-green-700 xl:text-[32px]">
                  {formatNumber(country.population)}
                </p>
                <div className="flex h-9 items-center justify-center rounded-[40px] bg-green-500 px-2.5 py-1">
                  <p className="text-base font-semibold text-white xl:text-lg">
                    {Math.min(
                      +(
                        country.population !== 0
                          ? (country.living / country.population) * 100
                          : 0
                      ).toFixed(2),
                      99.99
                    )}
                    %
                  </p>
                </div>
              </div>
              <div className="mt-1 flex gap-1 text-xs font-normal leading-none text-zinc-500">
                Total Population
                <InformationOutlineIcon fill="#a1a1aa" height={12} width={12} />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {!isEmpty(countryData) && (
        <div className="flex flex-wrap gap-2 px-[20px] max-md:bg-[#FAFAFA] md:gap-5 md:px-[45px] tablet:flex-nowrap tablet:px-[80px]">
          {[
            { title: 'Living', value: recordedPopulation },
            { title: 'Deceased', value: countryData.population.dead },
            { title: 'Average Age Years', value: countryData.average_age },
            {
              title: 'Completion Index',
              value: countryData.completion_index,
              tooltip:
                'Percentage of information we have on average for each human.',
            },
            {
              title: 'Certainty Index',
              value: countryData.certainty_index,
              tooltip:
                'The certainty index (1-100) is higher when data is official or corroborated by several sources.',
            },
          ].map((data, index) => (
            <div
              key={index}
              className={cn(
                'inline-flex flex-col items-start justify-start gap-1 rounded-xl bg-white p-6 grow shrink basis-0 shadow-[0px_2px_16px_-4px_rgba(9,9,11,0.08)]',
                index === 0
                  ? 'min-w-full flex-1 tablet:w-fit tablet:min-w-min'
                  : 'w-[calc(50%-10px)] min-w-[calc(50%-10px)] tablet:w-fit tablet:min-w-min'
              )}
            >
              <div className="flex flex-row items-center gap-1 text-sm font-normal leading-tight text-zinc-500">
                <p className="text-xs xl:text-sm">{data.title}</p>
                {data.tooltip ? (
                  <InformationOutlineIcon
                    id={data.title}
                    tooltip={data.tooltip}
                    fill="#a1a1aa"
                    height={12}
                    width={12}
                  />
                ) : null}
              </div>
              <div className="flex flex-col items-start justify-center gap-2 break-all">
                <div className="break-all text-xl font-semibold leading-loose text-zinc-950 xl:text-2xl">
                  {formatNumber(
                    !Number.isInteger(data.value) &&
                      typeof data.value === 'number'
                      ? data.value?.toFixed(2)
                      : data.value
                  )}
                </div>
              </div>
              {data.tooltip ? (
                <ReactTooltip
                  className="tooltip-opacity"
                  id={data.title}
                  type={'info'}
                  effect={'solid'}
                  place="top"
                  backgroundColor={'#ECECEC'}
                  textColor={'#454545'}
                />
              ) : null}
            </div>
          ))}
        </div>
      )}

      <RecordsStatsChart chartData={chartData} />

      <div className="mt-6 px-[20px] max-md:bg-[#FAFAFA] md:px-[45px] tablet:px-[80px]">
        {(firstNamesData?.length > 0 || lastNamesData?.length > 0) && (
          <div className="flex flex-col gap-6 bigTablet:flex-row">
            {firstNamesData?.length && (
              <ListWithLinks
                data={firstNamesData}
                title={'Popular first names'}
                href="/first-name"
                onClick={handleNameClick}
                page={page}
                nrOfElementsPerPage={pageSize}
                countryIso2={country?.iso_2?.toLowerCase()}
              />
            )}
            {lastNamesData?.length > 0 && (
              <ListWithLinks
                data={lastNamesData}
                href="/last-name"
                title={'Popular last names'}
                onClick={handleNameClick}
                page={page}
                nrOfElementsPerPage={pageSize}
                countryIso2={country?.iso_2?.toLowerCase()}
              />
            )}
          </div>
        )}
      </div>

      <div className="mt-6 px-[20px] max-md:bg-[#FAFAFA] md:px-[45px] tablet:px-[80px]">
        {(agePyramidData?.length > 0 || sexRatioDataFemale?.length > 0) && (
          <div className="flex flex-col gap-6 bigTablet:flex-row">
            {sexRatioDataFemale?.length > 0 && (
              <PieChartDiagram data={sexRatioDataFemale} title={'Sex ratio'} />
            )}
            {agePyramidData?.length > 0 && (
              <FunnelDiagram data={agePyramidData} title={'Age pyramid'} />
            )}
          </div>
        )}
      </div>

      <RecordedCountriesTable title="Popular searches" countries={countries} />

      <NavFooter
        className={`${
          !countryData?.top_first_names &&
          !countryData?.top_last_names &&
          !countryData?.age_pyramid
            ? 'mt-auto'
            : ''
        }`}
      />
    </div>
  );
}

export const getServerSideProps = async ({
  locale,
  params,
}: NextPageContext & { params: Params }) => {
  const cacheKey = `${process.env.KV_ENV}-country-data-${params?.slug}-${
    locale ?? 'en'
  }`;
  const cacheCountryKey = `${process.env.KV_ENV}-country-data-country-${
    params?.slug
  }-${locale ?? 'en'}`;

  const cachedTimestamp = await kv.get(
    `${process.env.KV_ENV}-cachedTimestamp-${params?.slug}`
  );
  const translations = await serverSideTranslations(locale ?? 'en', [
    'common',
    'about',
    'partners',
    'navbar',
    'new-homepage',
    'new-navbar',
    'new-footer',
    'country-slug',
  ]);
  const fifteenMinutes = 15 * 60 * 1000; // 15 minutes in milliseconds
  const now = Date.now();

  // Try to get data from KV
  const cachedData = await kv.get(cacheKey);
  const cachedCountries = await kv.get(`${process.env.KV_ENV}-countries`);
  let chartData = await kv.get(`${cacheCountryKey}-chartData`);

  if (
    cachedData &&
    chartData &&
    now - Number(cachedTimestamp) < fifteenMinutes
  ) {
    return {
      props: {
        ...translations,
        countryData: {
          ...(typeof cachedData === 'object' && cachedData !== null
            ? cachedData
            : {}),
        },
        countries: cachedCountries ?? [],
        country: (cachedCountries as [])?.find(
          (country: any) =>
            country.name?.toLowerCase() ===
            (params?.slug as string)?.toLowerCase().replaceAll('-', ' ')
        ),
        chartData: chartData ?? {},
      },
    };
  }

  // If not cached or cache expired, fetch new data
  let countries, countryResponse, countryEntry, topNamesData;
  try {
    [countries] = await Promise.all([searchForAllCountriesStaticProps()]);

    countryEntry = countries?.find(
      (country: any) =>
        country.name?.toLowerCase() ===
        (params?.slug as string)?.toLowerCase().replaceAll('-', ' ')
    );

    if (countryEntry) {
      countryResponse = await getCountryStaticProps(
        countryEntry?.iso_3,
        countryEntry?.api_public_domain_name
      );
    }

    [chartData] = await Promise.all([
      fetchChartData(false, countryEntry?.iso_3),
    ]);

    // Handle top names data
    topNamesData = await getCountryTopNamesStaticProps(
      countryEntry?.iso_3,
      countryEntry?.api_public_domain_name,
      '1',
      '100'
    );
  } catch (error) {
    console.log('Error fetching data:', error);
  }

  // Process the top names data only if it was successfully retrieved
  let topFirstNames = [],
    topLastNames = [];

  if (topNamesData) {
    if (Array.isArray(topNamesData?.data?.top_first_names)) {
      topFirstNames = topNamesData?.data?.top_first_names;
    } else if (Array.isArray(countryResponse?.top_first_names)) {
      topFirstNames = countryResponse?.top_first_names?.slice(0, 100);
    }

    if (Array.isArray(topNamesData?.data?.top_last_names)) {
      topLastNames = topNamesData?.data?.top_last_names;
    } else if (Array.isArray(countryResponse?.top_last_names)) {
      topLastNames = countryResponse?.top_last_names?.slice(0, 100);
    }

    let firstNameWithHighestCount: any;
    let lastNamesWithHighestCount: any;
    if (topFirstNames.length > 0) {
      firstNameWithHighestCount = topFirstNames.reduce(
        (maxName: any, currentName: any) => {
          return currentName.count > maxName.count ? currentName : maxName;
        },
        topFirstNames[0]
      );
    }
    if (topLastNames.length > 0) {
      lastNamesWithHighestCount = topLastNames.reduce(
        (maxName: any, currentName: any) => {
          return currentName.count > maxName.count ? currentName : maxName;
        },
        topLastNames[0]
      );
    }

    const firstNamesWithPercentage = topFirstNames.map(
      (currentObject: { name: string; count: number }) => ({
        name: currentObject.name,
        count: currentObject.count,
        percentage: +(
          (currentObject.count / firstNameWithHighestCount?.count) *
          100
        ).toFixed(2),
      })
    );

    const lastNamesWithPercentage = topLastNames.map(
      (currentObject: { name: string; count: number }) => ({
        name: currentObject.name,
        count: currentObject.count,
        percentage: +(
          (currentObject.count / lastNamesWithHighestCount?.count) *
          100
        ).toFixed(2),
      })
    );

    countryResponse.top_first_names = firstNamesWithPercentage || [];
    countryResponse.top_last_names = lastNamesWithPercentage || [];
  }

  const props = {
    ...(translations ?? {}),
    countries: countries ?? [],
    countryData: {
      ...(countryResponse ?? null),
      real_population: countryEntry?.population,
    },
    country: countryEntry ?? {},
    chartData: chartData ?? {},
  };

  await kv.set(cacheKey, {
    ...(countryResponse ?? null),
    real_population: countryEntry?.population,
  });
  await kv.set(`${process.env.KV_ENV}-countries`, props?.countries);
  await kv.set(cacheCountryKey, props.country);
  await kv.set(`${cacheCountryKey}-chartData`, chartData);
  await kv.set(
    `${process.env.KV_ENV}-cachedTimestamp-${params?.slug}`,
    now.toString()
  );
  return {
    props,
  };
};
